import React from 'react'
import { pdf } from '@react-pdf/renderer'
import { Page, Text, View, Document, StyleSheet, Font } from '@react-pdf/renderer'
import { doc, getDoc } from 'firebase/firestore'
import { db, functions } from '../firebaseConfig'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { uploadPdfFile } from './uploadPdfFile'
import { getEditHistoryEntries } from './editHistory'

Font.register({
    family: 'Poppins',
    fonts: [
        { src: require('../assets/fonts/Poppins-Regular.ttf') },
        { src: require('../assets/fonts/Poppins-SemiBold.ttf'), fontWeight: 'semibold' },
        { src: require('../assets/fonts/Poppins-Bold.ttf'), fontWeight: 'bold' },
        { src: require('../assets/fonts/Poppins-Italic.ttf'), fontStyle: 'italic' }
    ]
})

const styles = StyleSheet.create({
    page: {
        padding: 24,
        fontFamily: 'Poppins'
    },
    title: {
        fontSize: 20,
        textAlign: 'center',
        marginBottom: 20
    },
    header: {
        marginBottom: 8,
        fontSize: 10,
        flexDirection: 'row',
        justifyContent: 'space-between',
        textTransform: 'uppercase'
    },
    // Bagian BG: `border: 1` (shorthand) terbukti TIDAK konsisten me-render
    // sisi atas & kiri saat container ini membungkus baris-baris flex yang
    // masing-masing juga punya border sendiri (borderBottomWidth di tableRow) --
    // direproduksi & dikonfirmasi lewat render lokal. Sisi eksplisit per-arah
    // (borderTopWidth/dst) TIDAK kena bug yang sama.
    tableContainer: {
        borderTopWidth: 1,
        borderLeftWidth: 1,
        borderRightWidth: 1,
        borderBottomWidth: 1,
        borderColor: '#000'
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomColor: '#000',
        borderBottomWidth: 1,
        alignItems: 'stretch'
    },
    tableHeader: {
        textAlign: 'center'
    },
    tableHeaderCell: {
        fontSize: 8,
        padding: 4,
        alignItems: 'center',
        justifyContent: 'center',
        borderRightColor: '#000',
        borderRightWidth: 1,
        height: '100%'
    },
    tableCell: {
        fontSize: 8,
        padding: 4,
        borderRightColor: '#000',
        borderRightWidth: 1,
        height: '100%',
        justifyContent: 'center'
    },
    // Bagian BO: kotak rekening tujuan pencairan, disamakan persis dengan
    // `bankInfo` di ReimbursementPdf.jsx (borderWidth terpisah, bukan shorthand
    // `border` -- lihat catatan Bagian BG di tableContainer).
    bankInfo: {
        borderWidth: 2,
        fontWeight: 'semibold',
        padding: 4
    },
    footer: {
        marginTop: 8,
        fontSize: 8,
        padding: 4
    },
    footerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 32,
        paddingVertical: 0
    }
})

const getApprovedReviewerNames = async (bonSementaraDetail) => {
    if (!bonSementaraDetail || !bonSementaraDetail.statusHistory) {
        return { reviewer1Name: '-', reviewer2Name: '-' }
    }

    const { statusHistory } = bonSementaraDetail

    // Cek apakah Super Admin menggantikan kedua reviewer
    const superAdminForReviewer1 = statusHistory.find(history =>
        history.status.toLowerCase().includes('disetujui oleh super admin (pengganti reviewer 1)')
    )
    const superAdminForReviewer2 = statusHistory.find(history =>
        history.status.toLowerCase().includes('disetujui oleh super admin (pengganti reviewer 2)')
    )

    // Jika Super Admin menggantikan kedua reviewer, langsung return Super Admin
    if (superAdminForReviewer1 && superAdminForReviewer2) {
        return { reviewer1Name: 'Super Admin', reviewer2Name: '' }
    }

    // Cek approval untuk reviewer 1
    const reviewer1Approval = statusHistory.find(
        (history) =>
            history.status.toLowerCase().includes('disetujui oleh super admin (pengganti reviewer 1)') ||
            history.status.toLowerCase().includes('disetujui oleh reviewer 1')
    )

    // Cek approval untuk reviewer 2
    const reviewer2Approval = statusHistory.find(
        (history) =>
            history.status.toLowerCase().includes('disetujui oleh super admin (pengganti reviewer 2)') ||
            history.status.toLowerCase().includes('disetujui oleh reviewer 2')
    )

    let reviewer1Name = '-'
    let reviewer2Name = '-'

    // Set nama untuk reviewer 1
    if (reviewer1Approval) {
        if (reviewer1Approval.status.toLowerCase().includes('super admin')) {
            reviewer1Name = 'Super Admin'
        } else {
            try {
                const reviewer1DocRef = doc(db, 'userDirectory', reviewer1Approval.actor)
                const reviewer1Snapshot = await getDoc(reviewer1DocRef)
                if (reviewer1Snapshot.exists()) {
                    reviewer1Name = reviewer1Snapshot.data().nama
                }
            } catch (error) {
                console.error('Error fetching reviewer 1 name:', error)
            }
        }
    }

    // Set nama untuk reviewer 2
    if (reviewer2Approval) {
        if (reviewer2Approval.status.toLowerCase().includes('super admin')) {
            reviewer2Name = 'Super Admin'
        } else {
            try {
                const reviewer2DocRef = doc(db, 'userDirectory', reviewer2Approval.actor)
                const reviewer2Snapshot = await getDoc(reviewer2DocRef)
                if (reviewer2Snapshot.exists()) {
                    reviewer2Name = reviewer2Snapshot.data().nama
                }
            } catch (error) {
                console.error('Error fetching reviewer 2 name:', error)
            }
        }
    }

    // Jika reviewer2 tidak ada atau kosong
    if (reviewer2Name === '-' && reviewer1Name !== '-') {
        return { reviewer1Name, reviewer2Name: '' }
    }

    // Jika reviewer1 tidak ada atau kosong
    if (reviewer1Name === '-' && reviewer2Name !== '-') {
        return { reviewer1Name: reviewer2Name, reviewer2Name: '' }
    }

    return { reviewer1Name, reviewer2Name }
}

// Bagian AN: resolve nama editor (uid -> nama) untuk tiap entri riwayat edit
// di statusHistory, dipanggil sebelum render (react-pdf tidak bisa async di
// dalam komponen Document itu sendiri) -- sama pola dengan
// getApprovedReviewerNames di atas.
const getEditHistoryWithNames = async (bonSementaraDetail) => {
    const entries = getEditHistoryEntries(bonSementaraDetail?.statusHistory)
    if (entries.length === 0) return []

    return Promise.all(
        entries.map(async (entry) => {
            let actorName = 'N/A'
            try {
                const actorDocRef = doc(db, 'userDirectory', entry.actor)
                const actorSnapshot = await getDoc(actorDocRef)
                if (actorSnapshot.exists()) actorName = actorSnapshot.data().nama
            } catch (error) {
                console.error('Error fetching editor name:', error)
            }
            return { ...entry, actorName }
        })
    )
}

const BsPDF = ({ bonSementaraDetail, approvedReviewers, editHistory = [] }) => {
    if (!bonSementaraDetail || bonSementaraDetail.status !== 'Disetujui') {
        return null
    }

    // Fungsi untuk mengubah angka menjadi terbilang
    const terbilang = (angka) => {
        const bilangan = [
            '',
            'Satu',
            'Dua',
            'Tiga',
            'Empat',
            'Lima',
            'Enam',
            'Tujuh',
            'Delapan',
            'Sembilan',
            'Sepuluh',
            'Sebelas',
            'Dua Belas',
            'Tiga Belas',
            'Empat Belas',
            'Lima Belas',
            'Enam Belas',
            'Tujuh belas',
            'Delapan belas',
            'Sembilan belas'
        ]
        const satuan = ['', 'Ribu', 'Juta', 'Miliar', 'Triliun']

        if (angka < 20) return bilangan[angka]

        const prosesTerbilang = (num) => {
            if (num < 20) return bilangan[num]
            if (num < 100) {
                const depan = Math.floor(num / 10)
                const belakang = num % 10
                return (
                    (depan === 1 ? 'Sepuluh' : bilangan[depan] + ' Puluh') +
                    (belakang > 0 ? ' ' + bilangan[belakang] : '')
                )
            }
            if (num < 1000) {
                const depan = Math.floor(num / 100)
                const belakang = num % 100
                return (
                    (depan === 1 ? 'Seratus' : bilangan[depan] + ' Ratus') +
                    (belakang > 0 ? ' ' + prosesTerbilang(belakang) : '')
                )
            }
            return ''
        }

        const formatTerbilang = (num) => {
            if (num === 0) return 'nol'

            let result = ''
            let sisa = num
            let tingkat = 0

            while (sisa > 0) {
                const bagian = sisa % 1000
                if (bagian !== 0) {
                    const sebutan = prosesTerbilang(bagian)
                    result = sebutan + ' ' + satuan[tingkat] + ' ' + result
                }
                sisa = Math.floor(sisa / 1000)
                tingkat++
            }

            return result.trim() + ' Rupiah'
        }

        return formatTerbilang(angka)
    }

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                {bonSementaraDetail.bonSementara?.map((item) => (
                    <View style={styles.header}>
                        <View style={{ alignItems: 'center' }}>
                            <Text>{bonSementaraDetail.user?.unit || '-'}</Text>
                            <Text>MAKASSAR</Text>
                        </View>
                        <Text>{item.nomorBS || '-'}</Text>
                    </View>
                ))}

                <Text style={styles.title}>BON SEMENTARA</Text>

                {/* Table */}
                <View style={styles.tableContainer}>
                    {/* Tanggal Pembayaran Oleh Kasir Row */}
                    <View style={[styles.tableRow]}>
                        <View style={[styles.tableCell, { width: '29%', borderRight: 0 }]}>
                            <Text>Tanggal Pembayaran Oleh Kasir</Text>
                        </View>
                        <View style={[styles.tableCell, { width: '1%', borderRight: 0 }]}>
                            <Text>:</Text>
                        </View>
                        <View style={[styles.tableCell, { width: '70%', borderRight: 0 }]}>
                            <Text>
                                {bonSementaraDetail.tanggalPengajuan
                                    ? new Date(bonSementaraDetail.tanggalPengajuan).toLocaleDateString('id-ID', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric'
                                    })
                                    : '-'}
                            </Text>
                        </View>
                    </View>

                    {/* Diselesaikan paling lambat tanggal Row */}
                    <View style={[styles.tableRow]}>
                        <View style={[styles.tableCell, { width: '29%', borderRight: 0 }]}>
                            <Text>Diselesaikan paling lambat tanggal</Text>
                        </View>
                        <View style={[styles.tableCell, { width: '1%', borderRight: 0 }]}>
                            <Text>:</Text>
                        </View>
                        <View style={[styles.tableCell, { width: '70%', borderRight: 0 }]}>
                            <Text> </Text>
                        </View>
                    </View>

                    {/* Header Row */}
                    <View style={[styles.tableRow, styles.tableHeader]}>
                        <View style={[styles.tableHeaderCell, { width: '6%' }]}>
                            <Text>NO.</Text>
                        </View>
                        <View style={[styles.tableHeaderCell, { width: '74%' }]}>
                            <Text>ACTIVITIES NAME</Text>
                        </View>
                        <View style={[styles.tableHeaderCell, { width: '20%', borderRight: 0 }]}>
                            <Text>JUMLAH (IDR)</Text>
                        </View>
                    </View>

                    {/* Data Row */}
                    {bonSementaraDetail.bonSementara?.map((item, index) => (
                        <View key={index} style={[styles.tableRow, { borderBottom: 0 }]}>
                            <View style={[styles.tableCell, { width: '6%', textAlign: 'center' }]}>
                                <Text>{index + 1}</Text>
                            </View>
                            <View style={[styles.tableCell, { width: '74%' }]}>
                                <Text>{item.aktivitas}</Text>
                            </View>
                            <View style={[styles.tableCell, { width: '20%', textAlign: 'right', borderRight: 0 }]}>
                                <Text>{item?.jumlahBS.toLocaleString('en-US') ?? 'N/A'}</Text>
                            </View>
                        </View>
                    ))}

                    {/* Empty Row */}
                    <View style={[styles.tableRow, { borderBottom: 0 }]}>
                        <View style={[styles.tableCell, { width: '6%' }]}>
                            <Text> </Text>
                        </View>
                        <View style={[styles.tableCell, { width: '74%' }]}>
                            <Text> </Text>
                        </View>
                        <View style={[styles.tableCell, { width: '20%', borderRight: 0 }]}>
                            <Text> </Text>
                        </View>
                    </View>

                    {/* Terbilang Row */}
                    {bonSementaraDetail.bonSementara?.map((item) => (
                        <View style={[styles.tableRow]}>
                            <View style={[styles.tableCell, { width: '6%' }]}>
                                <Text> </Text>
                            </View>
                            <View style={[styles.tableCell, { width: '74%', textTransform: 'uppercase' }]}>
                                <Text>TERBILANG: {terbilang(item.jumlahBS || 0)}</Text>
                            </View>
                            <View style={[styles.tableCell, { width: '20%', borderRight: 0 }]}>
                                <Text> </Text>
                            </View>
                        </View>
                    ))}

                    {/* Total Row */}
                    {bonSementaraDetail.bonSementara?.map((item) => (
                        <View style={[styles.tableRow]}>
                            <View style={[styles.tableCell, { width: '6%', borderRight: 0 }]}>
                                <Text> </Text>
                            </View>
                            <View style={[styles.tableCell, { width: '74%', textAlign: 'right' }]}>
                                <Text>TOTAL:</Text>
                            </View>
                            <View style={[styles.tableCell, { width: '20%', borderRight: 0, textAlign: 'right' }]}>
                                <Text>{item.jumlahBS.toLocaleString('en-US') ?? 'N/A'}</Text>
                            </View>
                        </View>
                    ))}

                    {/* Footer */}
                    <View style={styles.footer}>
                        <View style={styles.footerRow}>
                            <View
                                style={{
                                    flex: 1,
                                    justifyContent: 'center'
                                }}
                            >
                                <Text>Diajukan oleh {bonSementaraDetail.user?.nama || '-'}</Text>
                                <Text
                                    style={{
                                        marginTop: '8px'
                                    }}
                                >
                                    Dokumen Ini Sudah Mendukung Signature Digital
                                </Text>
                                <View
                                    style={[
                                        styles.bankInfo,
                                        { flexDirection: 'row', columnGap: 4, alignItems: 'flex-start', alignSelf: 'flex-start', marginTop: 6 }
                                    ]}
                                >
                                    <View style={{ flexDirection: 'column', gap: 2, flexShrink: 0 }}>
                                        <Text>Nama Account</Text>
                                        <Text>Bank Penerima</Text>
                                        <Text>No Rekening</Text>
                                    </View>
                                    <View style={{ flexDirection: 'column', gap: 2, flexShrink: 0 }}>
                                        <Text>:</Text>
                                        <Text>:</Text>
                                        <Text>:</Text>
                                    </View>
                                    <View style={{ flexDirection: 'column', gap: 2, paddingRight: 32 }}>
                                        <Text>{bonSementaraDetail.user?.nama || '-'}</Text>
                                        <Text>{bonSementaraDetail.user?.bankName || '-'}</Text>
                                        <Text>{bonSementaraDetail.user?.accountNumber || '-'}</Text>
                                    </View>
                                </View>
                            </View>
                            <View
                                style={{
                                    flex: 1,
                                    flexDirection: 'column',
                                    alignItems: 'flex-end',
                                    justifyContent: 'center'
                                }}
                            >
                                <Text> </Text>
                                <Text
                                    style={{
                                        marginTop: 'auto'
                                    }}
                                >
                                    <Text>
                                        Approved By {approvedReviewers.reviewer1Name}
                                        {approvedReviewers.reviewer2Name ? ` & ${approvedReviewers.reviewer2Name}` : ''}
                                    </Text>
                                </Text>
                            </View>
                        </View>

                        {/* Bagian AN: riwayat edit -- cuma tampil kalau dokumen ini
                            pernah diedit admin setelah dibuat, supaya voucher yang
                            tidak pernah diedit tetap ringkas seperti sebelumnya. */}
                        {editHistory.length > 0 && (
                            <View style={{ marginTop: 12, borderTop: 1, borderTopColor: '#000', paddingTop: 4 }}>
                                <Text style={{ fontWeight: 'bold', marginBottom: 2 }}>
                                    Riwayat Edit ({editHistory.length}x):
                                </Text>
                                {editHistory.map((entry, index) => (
                                    <Text key={index} style={{ marginBottom: 2 }}>
                                        {new Date(entry.timestamp).toLocaleDateString('id-ID', {
                                            day: '2-digit', month: '2-digit', year: 'numeric'
                                        })} -- {entry.actorName} ({entry.status.replace('Data Diubah oleh ', '')}){entry.reason ? `: ${entry.reason}` : ''}
                                    </Text>
                                ))}
                            </View>
                        )}
                    </View>
                </View>
            </Page>
        </Document>
    )
}

const generateBsPDF = async (bonSementaraDetail) => {
    try {
        // Cek apakah Bon Sementara sudah disetujui
        if (bonSementaraDetail.status !== 'Disetujui') {
            toast.error('Bon Sementara belum disetujui')
            return
        }

        // Ambil nama reviewer sebelum PDF dirender
        const approvedReviewers = await getApprovedReviewerNames(bonSementaraDetail)
        const editHistory = await getEditHistoryWithNames(bonSementaraDetail)

        // Buat dokumen PDF
        const pdfBlob = await pdf(
            <BsPDF
                bonSementaraDetail={bonSementaraDetail}
                approvedReviewers={approvedReviewers}
                editHistory={editHistory}
            />
        ).toBlob()

        const sanitizedKategori = bonSementaraDetail.bonSementara[0].kategori.replace(/\//g, '_')

        const downloadURL = await uploadPdfFile(
            functions,
            `BonSementara/${sanitizedKategori}/${bonSementaraDetail.displayId}/${bonSementaraDetail.displayId}.pdf`,
            pdfBlob,
            { mode: 'workflowDoc', collectionName: 'bonSementara', docId: bonSementaraDetail.id }
        )
        return downloadURL
    } catch (error) {
        console.error('Gagal mengunduh:', error)
        toast.error('Gagal mengunduh Bon Sementara')
        return null
    }
}

    ; <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeOnClick pauseOnHover />

export { BsPDF, generateBsPDF }
